import { TableColumn } from "@kyan/ui";

import type { ComboInstrument } from "#/store/combo/comboInstruments";
import { type MarketPair } from "#/types/markets";
import {
  type Option,
  type OptionChainEntry,
  OptionType,
} from "#/types/options";
import { TradeDirection } from "#/types/trade";
import { isCallOptionType } from "#/utils/instrument.utils";
import { isBuy } from "#/utils/marketActionConverter";
import { match } from "#/utils/match";

import { callColumns, putColumns, strikeColumn } from "./OptionChain.constants";
import { type OptionChainColumnGroup } from "./OptionChain.types";

export const calculateDist = (rows: OptionChainEntry[], indexPrice: number) => {
  return rows.map((item) => ({
    ...item,
    dist: (item.strike - indexPrice) / indexPrice,
  }));
};

export const calculateAroundATM = (
  rows: OptionChainEntry[],
  indexPrice: number,
) => {
  const atmIndex = rows.findIndex((item) => item.strike >= indexPrice);

  if (atmIndex === -1) {
    const startIndex = Math.max(rows.length - 3, 0);
    return rows.slice(startIndex);
  }

  const startIndex = Math.max(atmIndex - 3, 0);
  const endIndex = Math.min(atmIndex + 4, rows.length);

  return rows.slice(startIndex, endIndex);
};

// BTC_USDC-27JUN25-105000-P:bid = instrumentName:side
export const getOptionChainGroupId = ({
  row,
  group,
  maturity,
  marketPair,
}: {
  row: OptionChainEntry;
  group: OptionChainColumnGroup;
  maturity: string;
  rowIndex: number;
  marketPair: MarketPair;
}) => `${marketPair}-${maturity}-${row.strike}-${group}`;

export const getGroupIdFromOption = (option: Option) => {
  return `${option.instrument_name}:${option.isBuy ? "ask" : "bid"}`;
};

export const getPriceIndicatorRenderConditions = ({
  data,
  row,
  rowIndex,
  marketIndexPrice,
}: {
  data: OptionChainEntry[];
  row: OptionChainEntry;
  rowIndex: number;
  marketIndexPrice: number;
}) => {
  const attachToBottom =
    marketIndexPrice > row.strike && rowIndex === data.length - 1;

  const shouldRender =
    marketIndexPrice < row.strike
      ? rowIndex === data.findIndex((item) => item.strike > marketIndexPrice)
      : attachToBottom;

  return { shouldRender, attachToBottom };
};

export const getGroupIdFromComboInstrument = (instrument: ComboInstrument) =>
  `${instrument.underlying}_USDC-${instrument.maturity}-${instrument.strike}-${instrument.optionType === "call" ? "C" : "P"}:${instrument.side === "buy" ? "ask" : "bid"}`;

export const getColumns = ({
  isMd,
  isLg,
  optionType,
  tradeDirection,
}: {
  isMd: boolean;
  isLg: boolean;
  optionType: OptionType;
  tradeDirection: TradeDirection;
}): TableColumn<OptionChainEntry, OptionChainColumnGroup>[] => {
  if (isLg) return [...callColumns, strikeColumn, ...putColumns];

  const isCall = isCallOptionType(optionType);
  const isBuyDirection = isBuy(tradeDirection);
  const optionTypeColumns = isCall ? [...callColumns] : [...putColumns];

  if (isMd) return [strikeColumn, ...optionTypeColumns];

  const removalColumnIds = match([
    [["callBid", "callIVBid", "callBidSize"], isCall && isBuyDirection],
    [["callAsk", "callIVAsk", "callAskSize"], isCall && !isBuyDirection],
    [["putBid", "putIVBid", "putBidSize"], !isCall && isBuyDirection],
    [["putAsk", "putIVAsk", "putAskSize"], !isCall && !isBuyDirection],
  ]);

  const sortColumnIds = match([
    [
      ["callBid", "callIVBid", "callBidSize", "callDelta"],
      isCall && !isBuyDirection,
    ],
    [
      ["callAsk", "callIVAsk", "callAskSize", "callDelta"],
      isCall && isBuyDirection,
    ],
    [
      ["putBid", "putIVBid", "putBidSize", "putDelta"],
      !isCall && !isBuyDirection,
    ],
    [
      ["putAsk", "putIVAsk", "putAskSize", "putDelta"],
      !isCall && isBuyDirection,
    ],
  ]);

  const moblieColumns = [...optionTypeColumns]
    .filter((column) => !removalColumnIds?.includes(column.id))
    .map((column) => ({
      ...column,
      group:
        `${isCall ? "C" : "P"}:${isBuyDirection ? "ask" : "bid"}` as OptionChainColumnGroup,
    }));

  return [
    strikeColumn,
    ...[
      ...(isCall ? [...moblieColumns].reverse() : [...moblieColumns]).sort(
        (a, b) => {
          const aIndex = sortColumnIds?.indexOf(a.id) ?? -1;
          const bIndex = sortColumnIds?.indexOf(b.id) ?? -1;
          return aIndex - bIndex;
        },
      ),
    ],
  ];
};
